import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader } from '@sdir/sds';
import Qualification from '../../Organisms/NewAppStartFlow/Qualification/Qualification';
import { useCurrentApplication } from '../../../services/hooks';
import { CERTREQ_ENGINE, ValidationEngine } from '../../../services/certreq/validationEngine';
import { getRouteForLanguage } from '../../../helpers/languageHelpers';
import { certReqRequirementsRoute, LEGACY_FALLBACK_PARAM } from './certreqRoutes';
import { applicationEngine, sammeUid } from './certreqEngine';

/*
 * `sammeUid` bor i `certreqEngine.ts` sammen med motoravlesningen, fordi
 * kravsiden trenger den til nøyaktig det samme. Den eksporteres videre herfra
 * slik at kallsteder som alltid har hentet den her, ikke må røres.
 */
export { sammeUid };

/**
 * Velger hvilken kvalifikasjonsside søknaden skal bruke.
 *
 * Søknaden er allerede lastet av CurrentApplicationProvider (ruta ligger under
 * ApplicationProviders), så dette koster ingen ekstra request. Mens den lastes
 * første gang viser vi en loader; feiler den, eller er motoren `legacy`, får
 * brukeren den gamle siden uendret. `?legacy=1` tvinger også den gamle siden —
 * den settes av kravsiden når kravtjenesten ikke kjenner sertifikatkoden.
 *
 * Motorvalget er KLEBRIG: første avgjorte valg huskes, og senere lasterunder
 * bytter ikke tilbake til loaderen. Grunnen er en remount-løkke: den gamle
 * kvalifikasjonssiden kaller `refreshApplication()` ved hver montering
 * (Qualification.tsx), og provideren setter `{loading:true}` og nuller `result`
 * mens den henter. Byttet vi da til loaderen, ville siden bli avmontert, montert
 * på nytt når svaret kom, friskoppe igjen — i det uendelige. Samme vaktpost-
 * mønster som i `useCertReqValidation`. Klebrigheten gjelder per søknad:
 * bytter `applicationUid`, avgjøres motoren på nytt.
 */
export const QualificationRouteSwitch: React.FC = () => {
  const intl = useIntl();
  const { applicationUid } = useParams();
  const [searchParams] = useSearchParams();
  const { application } = useCurrentApplication();

  const forceLegacy = searchParams.get(LEGACY_FALLBACK_PARAM) === '1';
  const uid = applicationUid || '';

  /** Første avgjorte motorvalg, med søknaden det gjelder for. */
  const decided = useRef<{ uid: string; engine: ValidationEngine } | null>(null);

  // Ny søknad — det gamle valget gjelder ikke lenger.
  if (decided.current && !sammeUid(decided.current.uid, uid)) decided.current = null;

  // `undefined` betyr «vet ikke ennå» — søknaden er ikke hentet, eller den som
  // ligger der er forrige søknad (provideren henter først i sin effekt, så rett
  // etter et uid-bytte er `result` fortsatt den gamle).
  const engineForUid = applicationEngine(application.result, uid);

  // Vi avgjør så snart søknaden foreligger, eller hentingen har feilet (feil
  // gir `legacy`, altså den gamle siden slik den alltid har vært).
  if (!decided.current) {
    if (engineForUid) {
      decided.current = { uid, engine: engineForUid };
    } else if (application.error) {
      decided.current = { uid, engine: 'legacy' };
    }
  }

  // `?legacy=1` overstyrer alltid — også et tidligere certreq-valg, slik at vi
  // ikke sender brukeren tilbake til kravsiden hen nettopp ble avvist fra.
  const engine: ValidationEngine | undefined = forceLegacy ? 'legacy' : decided.current?.engine;

  if (!engine) return <Loader />;

  if (engine === CERTREQ_ENGINE) {
    const path = generatePath(certReqRequirementsRoute, {
      applicationUid: uid || application.result?.uid || '',
    });
    return <Navigate to={getRouteForLanguage(intl.locale, path)} replace />;
  }

  return <Qualification />;
};

export default QualificationRouteSwitch;
